<template>
  <div class="md-editor">
    <!-- `capture`: com o popup aberto, as setas, o Enter e o Esc são dele, e não do texto — e o
         ProseMirror os ouve no próprio campo, depois de quem os pega na descida -->
    <div ref="host" @keydown.capture="onKeydown"/>
    <ontos-mention-picker v-if="picking && workspace" ref="picker" :query="picking.query" :target="picking.target"
                          :workspace="workspace" :idea="idea" @pick="insert($event)" @close="closePicker"/>
  </div>
</template>
<script lang="ts">
import {Component, Inject, Prop, Watch, toNative} from 'vue-facing-decorator'
import ToastHost from './ToastHost'
import MentionPicker, {MentionOption} from './MentionPicker.vue'
import {createEditor, Editor} from '@/utils/toastui'
import {WORKSPACE} from '@/components/shared/WorkspaceRouteComponent'
import {EnhancedWorkpace} from '@/types/Workspace'
import Idea from '@/types/Idea'
import {collapseMentions, expandMentions, fieldRanges, Mention, MentionContext, mentionHref} from '@/utils/markdownMentions'
import {mentionDecorations, mentionRenderer, mentionsInHtml} from '@/utils/markdownMentionView'
import {upload} from '@/services/mediaStorage'

type EditType = 'markdown' | 'wysiwyg'

/** O popup aberto: o que se digitou depois do `@` (`typed`: o `@` e as letras, a apagar ao escolher) e onde ele aparece. */
type Picking = { query: string, target: [number, number], typed: number }

/** O `@` e o que vem depois, logo antes do cursor — no começo da linha ou depois de um espaço. */
const TYPED_MENTION = /(?:^|\s)@([^\s@[\]]{0,40})$/

/**
 * Editor de markdown.
 *
 * Altura e estilo de preview são os padrões do wrapper do Vue 2 que este componente
 * substitui (300px, vertical), para o campo não mudar de tamanho.
 *
 * O conteúdo sai por métodos tipados, e não pelo `invoke('nome', …)` daquele wrapper:
 * quem usa chama `getMarkdown()`/`setMarkdown()` direto no ref.
 *
 * `emits` declarado: sem ele, o listener de `change` de quem usa cairia como atributo
 * no `<div>` raiz, e um `change` nativo vindo de dentro do editor — os campos das caixas
 * de link e de imagem — dispararia o handler uma segunda vez.
 *
 * **Com `mentions`** (o campo de markdown de um item, numa tela de ambiente), o editor fica no contexto
 * do app (pedido do usuário) — pelos recursos do próprio Toast UI:
 * - as referências (`[[cidade#5]]`, `[[cidade]]`, `[[@pessoa]]`) entram EXPANDIDAS, em link comum, e
 *   saem recolhidas (`markdownMentions`) — o `setMarkdown` e o `getMarkdown` cuidam da ida e da volta;
 * - o modo visual as desenha como a ficha (o plugin de decoração), e a prévia também (o renderizador);
 * - o `@` abre o popup no cursor, e o botão de @ da barra também.
 */
@Component({name: 'markdown-editor', emits: ['change', 'showMessage'], components: {'ontos-mention-picker': MentionPicker}})
class MarkdownEditor extends ToastHost<Editor> {
  @Prop({type: String, default: 'markdown'}) readonly initialEditType!: EditType
  @Prop({type: Boolean, default: false}) readonly mentions!: boolean
  /** O item dono do texto: os nomes de agora das referências (`_mentions`). */
  @Prop({type: Object, default: null}) readonly item!: Record<string, any> | null
  /** A coleção dele, para o `{{campo}}` do popup. */
  @Prop({type: String, default: ''}) readonly ideaAlias!: string
  @Inject({from: WORKSPACE, default: null}) readonly workspace!: EnhancedWorkpace | null

  picking: Picking | null = null

  get idea(): Idea | null {
    return (this.workspace && this.ideaAlias && this.workspace.ideaWith(this.ideaAlias)) || null
  }

  /** As referências valem só numa tela de ambiente, e só onde quem usa as pede. */
  get contextual(): EnhancedWorkpace | null {
    return this.mentions ? this.workspace : null
  }

  /**
   * Os nomes das referências: os de agora, que o servidor mandou com o item (`_mentions`), e os das que
   * se escolheram no popup desde que o editor abriu — no modo Markdown elas entram cruas, e a prévia
   * precisa do nome delas.
   */
  chosen: Record<string, Mention> = {}

  get mentionContext(): MentionContext | null {
    const workspace = this.contextual
    if (!workspace) return null
    const item = {...(this.item || {}), _mentions: {...((this.item && this.item._mentions) || {}), ...this.chosen}}
    return {workspace, item, idea: this.idea || undefined}
  }

  create(el: HTMLElement): Editor {
    const workspace = this.contextual
    const editor = createEditor(el, {
      initialEditType: this.initialEditType,
      height: '300px',
      previewStyle: 'vertical',
      events: {
        change: () => this.onChange(),
        //No modo Markdown o texto mostra a referência como se grava, `[[cidade#1]]` (pedido do usuário); no
        //visual, o link com o nome e a régua. Os três ganchos do Toast UI fazem a passagem: ao converter
        //para Markdown (também no `getMarkdown`), recolhe; ao voltar ao visual, expande; e a prévia ao lado
        //do Markdown desenha as referências cruas
        ...(workspace ? {
          beforeConvertWysiwygToMarkdown: (markdown: string) => collapseMentions(markdown, workspace.alias),
          beforePreviewRender: (html: string) => mentionsInHtml(html, this.mentionContext!)
        } : {})
      },
      ...(workspace ? {
        plugins: [mentionDecorations(() => this.mentionContext!)], customHTMLRenderer: mentionRenderer(workspace),
        //A imagem colada, solta ou escolhida no botão: sobe já ao rascunho, e o servidor a adota ao gravar
        hooks: {addImageBlobHook: (blob: Blob | File, insert: (url: string, text?: string) => void) => {
          this.uploadImage(blob, insert)
        }}
      } : {})
    })
    if (workspace) editor.on('changeMode', (mode: EditType) => {
      if (mode === 'wysiwyg') editor.setMarkdown(expandMentions(collapseMentions(editor.getMarkdown(), workspace.alias),
          this.mentionContext!), false)
    })
    //O primeiro da barra, pela importância (pedido do usuário)
    if (workspace) editor.insertToolbarItem({groupIndex: 0, itemIndex: 0},
        {name: 'mention', tooltip: this.$tt('mention.insert') as string, el: this.mentionButton()})
    return editor
  }

  /** O botão de @ da barra: o mesmo popup, para quem não conhece o `@`. */
  private mentionButton(): HTMLElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'toastui-editor-toolbar-icons md-mention-tool'
    button.innerHTML = '<i class="mdi mdi-at" aria-hidden="true"></i>'
    button.setAttribute('aria-label', this.$tt('mention.insert') as string)
    //Sem tomar o foco do texto ao pressionar: o `@` entra no cursor, e as letras seguem nele (relato do
    //usuário: o `@` entrava, mas o foco ficava no botão, e o que se digitava não ia a lugar nenhum)
    button.addEventListener('mousedown', event => event.preventDefault())
    button.addEventListener('click', () => this.openPicker())
    return button
  }

  getMarkdown(): string {
    const markdown = this.instance ? this.instance.getMarkdown() : ''
    return this.contextual ? collapseMentions(markdown, this.contextual.alias) : markdown
  }

  /** O texto como se grava; no modo visual, expandido em links — ver o `create`. */
  setMarkdown(value: string, cursorToEnd = true) {
    const context = this.mentionContext
    const visual = this.instance && this.instance.isWysiwygMode()
    this.instance?.setMarkdown(context && visual ? expandMentions(value, context) : value, cursorToEnd)
  }

  onChange() {
    this.$emit('change')
    if (!this.contextual) return
    this.followTyping()
    const markdown = this.getMarkdown()
    this.fieldTokens = fieldRanges(markdown).map(([from, to]) => markdown.slice(from, to)).join(' ')
  }

  /** Os `{{campo}}` do texto — para saber quando o valor desenhado de um deles muda (`drawnValues`). */
  fieldTokens = ''

  /**
   * O valor desenhado dos `{{campo}}` do texto, com o item como está no formulário: muda quando outro
   * campo muda — o nome do estado, o Sim —, e não quando se digita no próprio texto.
   */
  get drawnValues(): string {
    const context = this.mentionContext
    return context && this.fieldTokens ? mentionsInHtml(this.fieldTokens, context) : ''
  }

  /**
   * O markdown acompanha o formulário (pedido do usuário): mudou o valor de um campo citado, o modo visual
   * e a prévia se redesenham, sem mexer no texto nem no cursor. Antes, só na próxima edição do texto.
   *
   * Pelo que o Toast UI tem por dentro (`wwEditor`, `toastMark`, `eventEmitter`), que não tem API para
   * redesenhar — o `MarkdownEditor.test` o confere, e é ele que acusa uma versão que os mude:
   * - no modo visual, uma transação vazia: as decorações se refazem a cada uma (ver `mentionDecorations`),
   *   e sem mudança no texto ela não dispara o `change`;
   * - no Markdown, a prévia remontada inteira, pelo caminho do `setMarkdown` do visualizador do Toast UI
   *   (`editMarkdown` do texto todo e `updatePreview`); o texto do editor não muda.
   */
  @Watch('drawnValues')
  redraw() {
    const editor = this.instance as any
    if (!editor) return
    if (editor.isWysiwygMode()) {
      const view = editor.wwEditor.view
      view.dispatch(view.state.tr)
    } else {
      const lines: string[] = editor.toastMark.getLineTexts()
      const end = [lines.length, lines[lines.length - 1].length + 1]
      editor.eventEmitter.emit('updatePreview', editor.toastMark.editMarkdown([1, 1], end, lines.join('\n')))
    }
  }

  /** O `@` digitado abre o popup, e o que vem depois o filtra; apagado o `@`, ele fecha. */
  private followTyping() {
    const typed = this.typedMention()
    if (typed) this.picking = typed
    //Quem apagou o `@`, ou seguiu com um espaço, já decidiu o texto: fecha sem apagar nada
    else if (this.picking) {
      this.picking = null
      this.fromButton = false
    }
  }

  /** O `@consulta` logo antes do cursor, e onde o cursor está na tela. */
  private typedMention(): Picking | null {
    const match = TYPED_MENTION.exec(this.textBeforeCursor())
    if (!match) return null
    return {query: match[1], target: this.cursorPoint(), typed: match[1].length + 1}
  }

  /**
   * O texto logo antes do cursor — só o trecho que o `@consulta` pode ocupar —, pelo próprio editor. Era
   * pelo nó de texto do navegador, e no modo visual as decorações (o valor do campo, a foto) partem o
   * texto em mais de um nó. Com seleção, nada: o `@` não se digita sobre um trecho marcado.
   */
  private textBeforeCursor(): string {
    const editor = this.instance
    if (!editor) return ''
    //No modo visual a posição é um número; no de markdown, `[linha, coluna]`
    const [from, to] = editor.getSelection() as [number | [number, number], number | [number, number]]
    if (String(from) !== String(to)) return ''
    const reach = 42
    return Array.isArray(to) ? editor.getSelectedText([to[0], Math.max(1, to[1] - reach)], to)
        : editor.getSelectedText(Math.max(0, to - reach), to)
  }

  /** Onde o cursor está na tela, para o popup — pela seleção do navegador; sem ela, o canto do editor. */
  private cursorPoint(): [number, number] {
    const selection = window.getSelection()
    const rect = selection && selection.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : undefined
    if (rect && rect.bottom) return [rect.left, rect.bottom]
    const host = (this.$refs.host as HTMLElement).getBoundingClientRect()
    return [host.left + 16, host.top + 48]
  }

  /**
   * A imagem do markdown, no Storage, e não no texto (pedido do usuário). Antes ela entrava como
   * `data:image/…` dentro do item — o texto inteiro viajando em toda listagem —, e só o `migrate:images`
   * a tirava de lá.
   *
   * Sobe JÁ, para o rascunho do ambiente (`staging/…`), e aparece na hora; gravado o item, o servidor a
   * adota para a pasta dele, e a do formulário desistido some com a expiração do rascunho — ver o
   * `markdownImages` do servidor. Nunca órfã.
   */
  private async uploadImage(blob: Blob | File, insert: (url: string, text?: string) => void) {
    const workspace = this.contextual!
    const name = (blob as File).name || 'imagem'
    const safe = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9._-]/g, '-')
    try {
      insert(await upload(`staging/${workspace.alias}/${crypto.randomUUID()}-${safe}`, blob), name)
    } catch (err) {
      console.warn('Não foi possível enviar a imagem', err)
      this.$emit('showMessage', this.$tt('error.image-upload'))
    }
  }

  /**
   * Pelo botão: escreve o `@` no cursor, e daí é o caminho do digitado — as letras que vêm depois filtram,
   * como a dica dos itens pede (pedido do usuário: pelo botão, não havia onde digitar). Depois de uma
   * letra, com um espaço antes: o `@` colado numa palavra não abre o popup.
   */
  openPicker() {
    const editor = this.instance
    if (!editor) return
    editor.focus()
    const before = this.textBeforeCursor()
    editor.insertText(before && !/\s$/.test(before) ? ' @' : '@')
    this.fromButton = true
    this.followTyping()
    //O popup abre depois, e pode puxar o foco para si: ele volta ao texto
    this.$nextTick(() => editor.focus())
  }

  /** O `@` veio do botão: desistir o tira do texto, com o que se digitou depois (ver `closePicker`). */
  fromButton = false

  /**
   * Fecha o popup sem escolher — o Esc, o clique fora. O `@consulta` que o botão começou sai do texto: ele
   * era a busca, e não o que se queria escrever; o `@` digitado à mão fica, como qualquer texto.
   */
  closePicker() {
    //Conferido agora, e não pelo que o popup guardou: o clique fora pode ter levado o cursor dali
    const typed = this.fromButton && this.typedMention()
    if (typed) this.eraseTyped(typed.typed)
    this.picking = null
    this.fromButton = false
  }

  /** Apaga o `@consulta` (`typed` caracteres) antes do cursor — ao escolher, para a referência entrar no lugar; ao desistir do botão. */
  private eraseTyped(typed: number) {
    const editor = this.instance
    if (!editor || !typed) return
    //No modo visual a posição é um número; no de markdown, `[linha, coluna]`
    const [from, to] = editor.getSelection() as [any, any]
    editor.replaceSelection('', Array.isArray(from) ? [from[0], from[1] - typed] : from - typed, to)
  }

  onKeydown(event: KeyboardEvent) {
    const picker = this.$refs.picker as InstanceType<typeof MentionPicker> | undefined
    if (!this.picking || !picker) return
    const handled = event.key === 'ArrowDown' ? (picker.move(1), true)
        : event.key === 'ArrowUp' ? (picker.move(-1), true)
            : event.key === 'Enter' || event.key === 'Tab' ? picker.choose()
                : event.key === 'Escape' ? (this.closePicker(), true)
                    : false
    if (!handled) return
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * A escolha entra no lugar do `@consulta`: o link, com o nome, ou o `{{campo}}`. Apaga-se o digitado
   * antes, e o link entra com o cursor parado — com texto selecionado, o comando de link do modo visual
   * só marcaria o selecionado, em vez de pôr o nome.
   */
  insert(option: MentionOption) {
    const editor = this.instance
    const workspace = this.contextual
    if (!editor || !workspace) return
    this.eraseTyped(this.picking ? this.picking.typed : 0)
    this.picking = null
    this.fromButton = false
    if (option.kind === 'field') editor.insertText(`{{${option.alias}}}`)
    else {
      const href = mentionHref(option, workspace.alias)
      if (option.kind === 'item') this.chosen = {...this.chosen, [`${option.idea}#${option.instance}`]: {identifier: option.label}}
      //No modo Markdown, a referência como se grava; no visual, o link com o nome
      if (editor.isWysiwygMode()) editor.exec('addLink', {linkUrl: href, linkText: option.label})
      else editor.insertText(collapseMentions(`[x](${href})`, workspace.alias))
    }
    editor.insertText(' ')
    editor.focus()
  }
}

export default toNative(MarkdownEditor)
</script>

<style>
/* O botão de @ na barra do editor: o ícone do MDI no lugar do sprite do Toast UI, no mesmo tamanho.
   Sem margem: o Toast UI põe o botão próprio num invólucro (`toolbar-item-wrapper`) que já tem a margem
   dos outros, e a de botão da barra somava a ele — descia 7px (relato do usuário) */
.toastui-editor-defaultUI-toolbar .toastui-editor-toolbar-icons.md-mention-tool {
  display: block;
  margin: 0;
  background-image: none;
  font-size: 18px;
  line-height: 1;
  color: #555;
}
</style>
